// react
import { forwardRef, Ref, WheelEvent, useEffect, useState } from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import {
	Grid,
	InputAdornment,
	InputLabel,
	TextField,
	TextFieldProps,
	Typography,
} from '@mui/material';

// ui
import { IconCircle, IconCircleProps } from '../../ui/IconCircle/IconCircle';

// props
export type InputProps = Omit<TextFieldProps, 'ref'> & {
	currency?: string;
	hasSearchParam?: boolean;
	id?: string;
	maxLength?: number;
	ref?: Ref<HTMLInputElement>;
	type?: string;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			currency,
			disabled,
			defaultValue = '',
			fullWidth = true,
			hasSearchParam = false,
			id,
			InputProps = {},
			label,
			maxLength,
			name,
			onChange,
			required = false,
			size = 'small',
			type,
			variant = 'outlined',
			value,
			...props
		},
		ref
	) => {
		// defaults
		let defaultInputValue: TextFieldProps['value'] = '';

		if (value) {
			defaultInputValue = value;
		}

		if (defaultValue) {
			defaultInputValue = defaultValue;
		}

		// state
		const [searchParams, setSearchParams] = useSearchParams();
		const [inputValue, setInputValue] = useState(defaultInputValue);

		useEffect(() => {
			if (hasSearchParam && name) {
				const paramValue = searchParams.get(name);
				if (paramValue !== null) {
					setInputValue(paramValue);
				}
			}
		}, [hasSearchParam, name, searchParams]);

		const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			const newValue = event.target.value;
			if (onChange) onChange(event);
			setInputValue(newValue);
			if (hasSearchParam && name) {
				searchParams.set(name, newValue);
				setSearchParams(searchParams);
			}
		};

		const textFieldProps = {
			disabled,
			fullWidth,
			id,
			InputProps,
			inputRef: ref,
			name,
			onChange,
			required,
			size,
			type,
			value: inputValue,
			variant,
			...props,
		};

		const IconCircleProps: IconCircleProps = {
			color: 'success',
			type: 'check',
		};

		if (type === 'number') {
			textFieldProps.onWheel = (e: WheelEvent<HTMLDivElement>) => {
				const target = e.target as HTMLDivElement;
				return target.blur();
			};

			textFieldProps.InputProps = {
				...textFieldProps.InputProps,
				endAdornment: currency && <InputAdornment position="end">{currency}</InputAdornment>,
				inputProps: props.inputProps
					? props.inputProps
					: {
							min: 0,
							step: '0.01',
					  },
			};
		}

		if (maxLength) {
			// append maxLength to inputProps
			textFieldProps.InputProps = {
				...textFieldProps.InputProps,
				inputProps: {
					...textFieldProps.InputProps?.inputProps,
					maxLength,
				},
			};

			if (value) {
				const valueStr = value as string;
				if (valueStr.length > maxLength - 20) {
					IconCircleProps.color = 'warning';
					IconCircleProps.type = 'priority';
				}
			}
		}

		// update input value when value changes
		useEffect(() => {
			setInputValue(value);
		}, [value]);

		return (
			<Grid container spacing={1}>
				<Grid item xs={12}>
					{label && (
						<InputLabel disabled={disabled} htmlFor={id} required={required}>
							{label}
						</InputLabel>
					)}
					<TextField {...textFieldProps} onChange={handleChange} />
				</Grid>
				{maxLength && value && (
					<Grid item xs={12}>
						<Grid container justifyContent="flex-end">
							<Grid item>
								<Grid alignItems="center" container spacing={1}>
									<Grid item>
										<IconCircle {...IconCircleProps} size="xs" />
									</Grid>
									<Grid item>
										<Typography component="div" variant="caption">
											{(value as string).length} / {maxLength}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				)}
			</Grid>
		);
	}
);
